import React, { createContext, useContext, useEffect, useState } from 'react';
import { User, onAuthStateChanged, getRedirectResult } from 'firebase/auth';
import { doc, setDoc, onSnapshot } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { auth, db } from '../lib/firebase';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  userRole: 'stand' | 'festival' | 'promoter' | null;
  logOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  loading: true,
  userRole: null,
  logOut: async () => {}
});

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [userRole, setUserRole] = useState<'stand' | 'festival' | 'promoter' | null>(null);
  const navigate = useNavigate();

  const logOut = async () => {
    await auth.signOut();
    setUserRole(null);
  };

  useEffect(() => {
    let unsubscribeUserDoc: (() => void) | null = null;

    const loadingTimeout = setTimeout(() => setLoading(false), 6000);

    // Traite le retour de redirection Google (mobile) et navigue vers /home.
    getRedirectResult(auth).then((result) => {
      if (result?.user) navigate('/home');
    }).catch((e) => console.warn('getRedirectResult error:', e?.code || e));

    const unsubscribeAuth = onAuthStateChanged(auth, async (currentUser) => {
      setUser(currentUser);

      if (currentUser) {
        // Rôle depuis le cache sessionStorage pour un affichage immédiat.
        const cachedRole = sessionStorage.getItem(`userRole_${currentUser.uid}`);
        if (cachedRole === 'stand' || cachedRole === 'festival' || cachedRole === 'promoter') {
          setUserRole(cachedRole);
          // Si on a déjà le rôle en cache, on peut débloquer l'app tout de suite.
          clearTimeout(loadingTimeout);
          setLoading(false);
        }

        const userDocRef = doc(db, 'users', currentUser.uid);
        unsubscribeUserDoc = onSnapshot(userDocRef, async (userDoc) => {
          if (userDoc.exists()) {
            const role = userDoc.data().role as 'stand' | 'festival' | 'promoter';
            if (role) {
              sessionStorage.setItem(`userRole_${currentUser.uid}`, role);
              setUserRole(role);
            }
          } else {
            // Première connexion (ex. retour de redirection Google).
            const fallbackRole = (
              sessionStorage.getItem(`userRole_${currentUser.uid}`) ||
              localStorage.getItem('pendingSignupRole') ||
              'festival'
            ) as 'stand' | 'festival' | 'promoter';
            try {
              await setDoc(userDocRef, {
                name: currentUser.displayName || currentUser.email?.split('@')[0] || 'Utilisateur',
                email: currentUser.email || '',
                role: fallbackRole,
                createdAt: new Date().toISOString()
              });
              sessionStorage.setItem(`userRole_${currentUser.uid}`, fallbackRole);
              localStorage.removeItem('pendingSignupRole');
              setUserRole(fallbackRole);
            } catch (err) {
              console.error("Erreur d'initialisation du compte", err);
            }
          }
          clearTimeout(loadingTimeout);
          setLoading(false);
        }, (error) => {
          console.error("Erreur onSnapshot utilisateur:", error);
          clearTimeout(loadingTimeout);
          setLoading(false);
        });

      } else {
        if (unsubscribeUserDoc) {
          unsubscribeUserDoc();
          unsubscribeUserDoc = null;
        }
        setUserRole(null);
        clearTimeout(loadingTimeout);
        setLoading(false);
      }
    });

    return () => {
      clearTimeout(loadingTimeout);
      unsubscribeAuth();
      if (unsubscribeUserDoc) unsubscribeUserDoc();
    };
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0d0e12] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-[#ff5a00]/30 border-t-[#ff5a00] rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{ user, loading, userRole, logOut }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
